import { Component, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {MatLegacyPaginator as MatPaginator} from '@angular/material/legacy-paginator';
import { EnvService } from '../services/env.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { debounceTime } from 'rxjs';
import { SettingsPersistenceService } from '../services/settings-persistence.service';
import { AuthService } from '../services/auth.service';
import { NetworkProfilesFilter } from './cmdb-network-profile.filter';

@Component({
  selector: 'micro-network-profile',
  templateUrl: './cmdb-network-profile.component.html',
})
export class CmdbNetworkProfilesComponent {
  dataSource = new MatTableDataSource<any>();
  hostingPlatformNames = new Map<string, string>();
  classNames = new Map<string, string>();
  subnetNames = new Map<string, string>();

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  pageSettings!: SettingsPersistenceService;
  filter = new NetworkProfilesFilter(this.auth);

  constructor(public auth: AuthService, private env: EnvService, private http: HttpClient) {
  }

  ngOnInit(): void {
    // Setup the settings persistence service for this page
    this.pageSettings = new SettingsPersistenceService(sessionStorage, 'micro.networkProfiles');
    this.pageSettings
      .setFilter(this.filter)
      .setPaginator(this.paginator)
      .init();

    // Subscribe to filter changes with a debounce time to avoid excessive reloads
    this.filter.anyChange.pipe(debounceTime(500)).subscribe(change => {
      this.reload();
    });

    // Initial data load
    this.reload();
  }  

  ngOnDestroy(): void {
  }

  reload(): void {
    const params: HttpParams = this.filter.getParams()
      .set('page', String(this.paginator.pageIndex || 0))
      .set('size', String(this.paginator.pageSize || 20));

    this.http.get(`${this.env.e.url}/domain/cmdbNetworkProfiles`, {params}).subscribe(data => {
      const page = data as any;
      this.dataSource.data = page.content;
      this.loadHostingPlatforms(page.content);
      this.loadClassCodes(page.content);
      this.loadSubnets(page.content);
      this.paginator.pageIndex = page.number;
      this.paginator.pageSize = page.size;
      this.paginator.length = page.totalElements;
    });
  }

  private loadHostingPlatforms(networkProfile: any[]): void {
    const hostingPlatformIds = [...new Set(networkProfile
      .map(np => np.hostingPlatformId)
      .filter(id => id && !this.hostingPlatformNames.has(id)))] as string[];

    hostingPlatformIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/cmdbHostingPlatform/${id}`).subscribe({
        next: (hostingPlatform: any) => {
          this.hostingPlatformNames.set(id, hostingPlatform ? hostingPlatform.name : '');
        },
        error: () => {
          this.hostingPlatformNames.set(id, '');
        }
      });
    });
  }

  private loadClassCodes(networkProfile: any[]): void {
    const classIds = [...new Set(networkProfile
      .map(np => np.classCodeId)
      .filter(id => id && !this.classNames.has(id)))] as string[];

    classIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/cmdbClassCodes/${id}`).subscribe({
        next: (classCode: any) => {
          this.classNames.set(id, classCode ? classCode.name : '');
        },
        error: () => {
          this.classNames.set(id, '');
        }
      });
    });
  }

  private loadSubnets(networkProfile: any[]): void {
    const subnetIds = [...new Set(networkProfile
      .map(np => np.subnetId)
      .filter(id => id && !this.subnetNames.has(id)))] as string[];

    subnetIds.forEach(id => {
      this.http.get(`${this.env.e.url}/domain/cmdbSubnets/${id}`).subscribe({
        next: (subnet: any) => {
          this.subnetNames.set(id, subnet ? subnet.name : '');
        },
        error: () => {
          this.subnetNames.set(id, '');
        }
      });
    });
  }
}
